import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useNotifications } from '../context/NotificationContext';
import {
  NotificationCategory, NotificationItem,
  getNotifications, markAllNotificationsRead, markNotificationRead, markNotificationUnread,
  markNotificationSeen, markNotificationClicked, acknowledgeNotification, dismissNotification,
} from '../composables/notificationAPI';

function isExternalUrl(url: string): boolean {
  try {
    return new URL(url, window.location.origin).origin !== window.location.origin;
  } catch {
    return false;
  }
}

// Logic ของฟีด/interaction ใช้ร่วมกันระหว่าง dropdown บน desktop และหน้าเต็มบน mobile
export function useNotificationFeed(onNavigate?: () => void) {
  const { feedVersion, notifyLocalChange, sseStatus } = useNotifications();
  const navigate = useNavigate();

  const [items, setItems] = useState<NotificationItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [category, setCategory] = useState<NotificationCategory | ''>('');
  const [unreadOnly, setUnreadOnly] = useState(false);

  // เก็บจำนวนรายการที่โหลดไว้แล้ว (ผ่านปุ่ม "โหลดเพิ่มเติม") ไว้ใน ref เพื่อใช้ตอน reset แบบเงียบ ๆ
  // จาก SSE hint (ทุก ~30s) โดยไม่ทำให้ loadFeed ถูกสร้างใหม่ทุกครั้งที่ items เปลี่ยน
  const itemsCountRef = useRef(0);
  useEffect(() => { itemsCountRef.current = items.length; }, [items]);

  const loadFeed = useCallback(async (reset: boolean, silent = false) => {
    if (reset && !silent) setLoading(true);
    if (!reset) setLoadingMore(true);
    try {
      const data = await getNotifications({
        // reset ปกติ (เปิดครั้งแรก/เปลี่ยน filter) เริ่มที่ 20 ตามเดิม แต่ reset แบบเงียบจาก SSE ต้องขอเท่าที่เคยโหลดไว้
        // ไม่งั้น list ที่ผู้ใช้กด "โหลดเพิ่มเติม" ไปหลายหน้าจะถูกตัดกลับเหลือ 20 กลางอากาศ ทำให้ scroll เด้ง/จอกระพริบขาว
        limit: reset ? Math.min(100, Math.max(20, itemsCountRef.current)) : 20,
        category: category || undefined,
        unread: unreadOnly || undefined,
        cursor: reset ? undefined : cursor,
      });
      setItems((prev) => (reset ? data.items : [...prev, ...data.items]));
      setCursor(data.nextCursor);
      // ทำเครื่องหมาย "เห็นแล้ว" แบบเงียบ ๆ ให้รายการที่เพิ่งโหลดมาแสดง
      data.items.filter((n) => !n.state?.seenAt).forEach((n) => { void markNotificationSeen(n.id); });
    } catch (err: any) {
      // รีเฟรชเงียบ ๆ (จาก SSE ทุก ~30s) ไม่ toast error กันสแปม — เจ้าของกดโหลดเองค่อย toast
      if (!silent) toast.error(err.message || 'โหลดแจ้งเตือนไม่สำเร็จ');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [category, unreadOnly, cursor]);

  // เปลี่ยน filter เอง (หรือเปิดครั้งแรก) — โหลดแบบเห็น loading ได้ตามปกติ
  useEffect(() => { void loadFeed(true); }, [category, unreadOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  // SSE ส่ง refresh hint ทุก ~30s แม้ไม่มีอะไรใหม่ — รีเฟรชเบื้องหลังแบบเงียบ ไม่ทำให้ลิสต์กระพริบหาย
  const isFirstFeedVersion = useRef(true);
  useEffect(() => {
    if (isFirstFeedVersion.current) { isFirstFeedVersion.current = false; return; }
    void loadFeed(true, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedVersion]);

  const updateItem = (id: string, patch: Partial<NotificationItem>) => {
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, state: { ...n.state, ...patch.state } } : n)));
  };

  const toggleRead = async (n: NotificationItem) => {
    try {
      if (n.state?.readAt) {
        await markNotificationUnread(n.id);
        updateItem(n.id, { state: { readAt: null } });
      } else {
        await markNotificationRead(n.id);
        updateItem(n.id, { state: { readAt: new Date().toISOString() } });
      }
      notifyLocalChange();
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const acknowledge = async (n: NotificationItem) => {
    try {
      await acknowledgeNotification(n.id);
      updateItem(n.id, { state: { acknowledgedAt: new Date().toISOString() } });
      notifyLocalChange();
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const dismiss = async (n: NotificationItem) => {
    try {
      await dismissNotification(n.id);
      setItems((prev) => prev.filter((x) => x.id !== n.id));
      notifyLocalChange();
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const open = async (n: NotificationItem, url: string) => {
    try {
      await markNotificationClicked(n.id);
      await markNotificationRead(n.id);
      updateItem(n.id, { state: { readAt: new Date().toISOString() } });
      notifyLocalChange();
    } catch {
      // ให้เปิดลิงก์ต่อได้แม้บันทึก interaction ไม่สำเร็จ
    }
    if (isExternalUrl(url)) {
      const win = window.open(url, '_blank');
      if (win) win.opener = null;
    } else {
      const parsed = new URL(url, window.location.origin);
      navigate(parsed.pathname + parsed.search);
      onNavigate?.();
    }
  };

  const markAllRead = async () => {
    try {
      const res = await markAllNotificationsRead();
      notifyLocalChange();
      void loadFeed(true);
      toast.success(`อ่านแล้ว ${res.changed ?? ''} รายการ`);
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  return {
    items, cursor, loading, loadingMore, category, setCategory, unreadOnly, setUnreadOnly,
    loadFeed, toggleRead, acknowledge, dismiss, open, markAllRead, sseStatus,
  };
}
